import { useEffect, useMemo, useRef, useState } from "react";
import apiFetch from "./utils/api";
import Typeahead from "./components/Typeahead";
import "./ReportsPage.css";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-balham.css";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import ResourceChart from "./ResourceChart";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
);

ModuleRegistry.registerModules([AllCommunityModule]);

const ResourceOverview = ({ onClose } = {}) => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [resources, setResources] = useState([]);

  const [selectedResource, setSelectedResource] = useState(null);
  const [selectedLoading, setSelectedLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(null); // 1-12 or null
  const HOURS_PER_DAY = 7.5;
  const [subcodeBreakdown, setSubcodeBreakdown] = useState(null);
  const [projectSubcodeMatrix, setProjectSubcodeMatrix] = useState(null);
  const [divisionSummaries, setDivisionSummaries] = useState({});
  const [divisionColumns, setDivisionColumns] = useState([]);
  const [projects, setProjects] = useState([]);
  const gridRef = useRef(null);

  const [displayDays, setDisplayDays] = useState(true);
  const toggleDisplayDays = () => setDisplayDays(!displayDays);

  const fetchReport = async (y) => {
    setLoading(true);
    setError("");
    setSelectedResource(null);

    try {
      const body = await apiFetch(`/api/reports?year=${y}`);
      setRows(body.data || []);
    } catch (e) {
      setError(e.message || "Failed to load resource overview");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport(year);
  }, [year]);

  // Aggregate timesheet hours by division for each resource
  useEffect(() => {
    if (!rows || rows.length === 0) {
      setDivisionSummaries({});
      setDivisionColumns([]);
      return;
    }
    (async () => {
      try {
        const resp = await apiFetch(
          `/api/reports/user-split?year=${year}${selectedMonth != null ? "&month=" + selectedMonth : ""}`,
        );
        console.log(resp);
        //   resourcesInRows.map(async (resCode) => {
        //     try {
        //       const userObj =
        //         resources && resources.length
        //           ? resources.find((u) => u.code === resCode)
        //           : null;
        //       const userParam = userObj
        //         ? encodeURIComponent(userObj.id)
        //         : encodeURIComponent(resCode);
        //       const qs = `user=${userParam}&start=${range.start}&end=${range.end}`;
        //       const resp = await apiFetch(`/api/timesheets?${qs}`);
        //       const entries = Array.isArray(resp) ? resp : [];
        //       const divMap = {};
        //       let total = 0;
        //       for (const e of entries) {
        //         // Resolve division from the project record (dbo.project.projectdivision)
        //         // where possible. Use e.ProjectCode / e.Project to find the project.
        //         const projectCode =
        //           e.ProjectCode || e.Project || e.project || null;
        //         let division = "Unspecified";
        //         if (projectCode && projects && projects.length) {
        //           const pj = projects.find(
        //             (p) =>
        //               String(
        //                 p.code || p.ProjectCode || p.ProjectID || "",
        //               ).toLowerCase() === String(projectCode).toLowerCase(),
        //           );
        //           if (pj) {
        //             division = (
        //               pj.division ||
        //               pj.ProjectDivision ||
        //               pj.div ||
        //               "Unspecified"
        //             )
        //               .toString()
        //               .trim();
        //           }
        //         }
        //         // fallback: if project lookup failed, try entry.ProjectDivision (if present)
        //         if (!division || division === "Unspecified") {
        //           const fallback = e.ProjectDivision || e.Division || null;
        //           if (fallback) division = String(fallback).trim();
        //         }
        //         const hours =
        //           Number(e.TimesheetHours || e.Hours || e.hours || 0) || 0;
        //         total += hours;
        //         divMap[division] = (divMap[division] || 0) + hours;
        //         divisionsSet.add(division);
        //       }
        //       summaries[resCode] = {
        //         divisions: divMap,
        //         totalHours: total,
        //         //samples: entries,
        //       };
        //     } catch (e) {
        //       summaries[resCode] = { divisions: {}, totalHours: 0 };
        //     }
        //   }),
        // );

        setDivisionSummaries(resp.summaries);
        setDivisionColumns(resp.divisions.sort());
      } catch (e) {
        setDivisionSummaries({});
        setDivisionColumns([]);
      }
    })();
  }, [rows, year, selectedMonth, resources, projects]);

  useEffect(() => {
    (async () => {
      setSelectedLoading(true);
      if (!selectedResource) {
        setSubcodeBreakdown(null);
        setProjectSubcodeMatrix(null);
        return;
      }
      try {
        const r = encodeURIComponent(selectedResource.id);
        const monthParam = selectedMonth ? `&month=${selectedMonth}` : "";
        const res = await apiFetch(
          `/api/reports/resource-subcodes?year=${year}&resource=${r}${monthParam}`,
        );
        setSubcodeBreakdown(res && res.breakdown ? res.breakdown : null);
        const res2 = await apiFetch(
          `/api/reports/resource-project-subcodes?year=${year}&resource=${r}${monthParam}`,
        );
        setProjectSubcodeMatrix(res2 && res2.matrix ? res2 : null);
      } catch (e) {
        setSubcodeBreakdown(null);
        setProjectSubcodeMatrix(null);
      }
      setSelectedLoading(false);
    })();
  }, [selectedResource, year, selectedMonth]);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch("/api/users");
        const list = Array.isArray(res)
          ? res.users || res
          : res && Array.isArray(res.users)
            ? res.users
            : [];
        // normalize to { code, desc } so Typeahead filters by code/desc like projects
        setResources(
          list.map((u) => ({
            id: u.UserID,
            code: u.UserCode,
            desc: u.UserDescription,
          })),
        );
      } catch (e) {
        // ignore
      }
    })();
    // load projects for resolving project -> division mapping
    (async () => {
      try {
        const resp = await apiFetch("/api/projects");
        const plist = Array.isArray(resp)
          ? resp
          : resp && Array.isArray(resp.projects)
            ? resp.projects
            : [];
        setProjects(plist);
      } catch (e) {
        // ignore
      }
    })();
  }, []);

  const tableRows = useMemo(() => {
    return rows
      .filter(
        (r) =>
          (!selectedResource ||
            r.resource === (selectedResource.code || selectedResource)) &&
          (divisionSummaries[r.resource] || { totalHours: 0 }).totalHours > 0,
      )
      .map((r) => {
        const sums = divisionSummaries[r.resource] || {
          divisions: {},
          totalHours: 0,
        };
        const row = {
          resource: r.resource,
          totalHours: Number(sums.totalHours || 0),
          totalDays: Number(sums.totalHours || 0) / HOURS_PER_DAY,
        };

        divisionColumns.forEach((division, index) => {
          const hours = Number(
            (sums.divisions && sums.divisions[division]) || 0,
          );
          const total = Number(sums.totalHours || 0);
          row[`division_${index}`] = total > 0 ? (hours / total) * 100 : 0;
          row[`division_${index}_hours`] = hours;
        });

        return row;
      });
  }, [rows, selectedResource, divisionSummaries, divisionColumns]);

  const columnDefs = useMemo(() => {
    return [
      {
        field: "resource",
        headerName: "Resource",
        minWidth: 140,
        flex: 1.4,
        pinned: "left",
      },
      ...divisionColumns.map((division, index) => {
        const isCorp =
          typeof division === "string" &&
          division.trim().toLowerCase() === "corporate admin";
        return {
          field: `division_${index}`,
          headerName: isCorp ? "CORP" : division,
          minWidth: 100,
          flex: 1,
          type: "numericColumn",
          tooltipValueGetter: (params) =>
            `${Number(params.data?.[`division_${index}_hours`] || 0).toFixed(2)} hrs`,
          cellStyle: (params) => ({
            backgroundColor: `color-mix(in oklab, #f4f4f4, #56a6be ${Number(params.value || 0).toFixed(1)}%)`,
            textAlign: "right",
          }),
          valueFormatter: (params) =>
            `${Number(params.value || 0).toFixed(1)}%`,
        };
      }),
      {
        colId: "totalMetric",
        field: displayDays ? "totalDays" : "totalHours",
        headerName: displayDays ? "Total (Days)" : "Total (Hours)",
        minWidth: 120,
        flex: 1,
        type: "numericColumn",
        valueFormatter: (params) =>
          displayDays
            ? Number(params.value || 0).toFixed(2)
            : String(Number(params.value || 0)),
        cellStyle: { textAlign: "right", fontWeight: 700 },
      },
    ];
  }, [divisionColumns, displayDays]);

  function exportToCSV() {
    const api = gridRef.current?.api;
    if (!api) return;

    const resourceCode = selectedResource?.code
      ? `-${selectedResource.code}`
      : "";
    const monthSuffix = selectedMonth ? `-month-${selectedMonth}` : "";
    api.exportDataAsCsv({
      fileName: `resource-split-${year}${monthSuffix}${resourceCode}.csv`,
    });
  }

  function exportToPDF() {
    try {
      const baseTitle = `Resource Split ${selectedResource ? selectedResource.code : ""} ${year}${selectedMonth ? ` Month ${selectedMonth}` : ""}`;
      const now = new Date();
      const dateStr = now.toISOString().slice(0, 10);
      const title = `${baseTitle} ${dateStr}`;

      const headerHtml = divisionColumns
        .map((division) => {
          const isCorp =
            typeof division === "string" &&
            division.trim().toLowerCase() === "corporate admin";
          const label = isCorp ? "CORP" : division;
          return `<th style="border:1px solid #ddd;padding:6px;text-align:left;">${label}</th>`;
        })
        .join("");

      const rowsHtml = (tableRows || [])
        .map((row) => {
          const divisionCells = divisionColumns
            .map((division, index) => {
              const percent = Number(row[`division_${index}`] || 0);
              const hours = Number(row[`division_${index}_hours`] || 0);
              const bg = `color-mix(in oklab, #f4f4f4, #56a6be ${percent.toFixed(1)}%)`;
              return `<td style="border:1px solid #eee;padding:6px;text-align:right;background:${bg};" title="${hours.toFixed(2)} hrs">${percent.toFixed(1)}%</td>`;
            })
            .join("");

          const totalValue = displayDays
            ? Number(row.totalDays || 0).toFixed(2)
            : String(Number(row.totalHours || 0));
          return `<tr>
            <td style="border:1px solid #eee;padding:6px;">${row.resource || ""}</td>
            ${divisionCells}
            <td style="border:1px solid #eee;padding:6px;text-align:right;font-weight:700;">${totalValue}</td>
          </tr>`;
        })
        .join("");

      const doc = window.open("", "_blank");
      if (!doc) throw new Error("Unable to open print window (popup blocked)");
      doc.document.write(`<!doctype html><html><head><title>${title}</title>`);
      try {
        doc.document.title = title;
      } catch (e) {}
      doc.document.write(
        `<style>body{font-family:Arial,Helvetica,sans-serif;padding:20px} table{border-collapse:collapse;width:100%} th,td{border:1px solid #ddd;padding:6px}</style>`,
      );
      doc.document.write(`</head><body>`);
      doc.document.write(`<h2>${baseTitle}</h2>`);
      doc.document.write(
        `<table><thead><tr><th style="border:1px solid #ddd;padding:6px;text-align:left;">Resource</th>${headerHtml}<th style="border:1px solid #ddd;padding:6px;text-align:left;">${displayDays ? "Total (Days)" : "Total (Hours)"}</th></tr></thead><tbody>${rowsHtml}</tbody></table>`,
      );
      doc.document.write(`</body></html>`);
      doc.document.close();
      setTimeout(() => {
        try {
          doc.print();
        } catch (e) {
          console.warn("Print failed:", e);
        }
        doc.close();
      }, 600);
    } catch (e) {
      console.error(e);
      alert("Export failed: " + (e && e.message ? e.message : String(e)));
    }
  }

  return (
    <div style={{ padding: 20 }}>
      <h2>Resource Split</h2>

      <div
        style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <label style={{ fontWeight: 600 }}>Year</label>
        <button onClick={() => setYear((y) => y - 1)}>-</button>
        <input
          type="number"
          value={year}
          onChange={(e) =>
            setYear(Number(e.target.value || new Date().getFullYear()))
          }
          style={{ width: 100 }}
        />
        <button onClick={() => setYear((y) => y + 1)}>+</button>
        <div style={{ marginLeft: 12 }}>
          <label style={{ fontWeight: 600, marginRight: 8 }}>Month</label>
          <select
            value={selectedMonth || ""}
            onChange={(e) =>
              setSelectedMonth(e.target.value ? Number(e.target.value) : null)
            }
            style={{ width: 160 }}
          >
            <option value="">All</option>
            <option value="1">January</option>
            <option value="2">February</option>
            <option value="3">March</option>
            <option value="4">April</option>
            <option value="5">May</option>
            <option value="6">June</option>
            <option value="7">July</option>
            <option value="8">August</option>
            <option value="9">September</option>
            <option value="10">October</option>
            <option value="11">November</option>
            <option value="12">December</option>
          </select>
        </div>
        <div style={{ marginLeft: 12 }}>
          <button onClick={() => fetchReport(year)}>Refresh</button>
        </div>
        <div style={{ marginLeft: 12 }}>
          <button onClick={exportToPDF}>Export to PDF</button>
        </div>
        <div style={{ marginLeft: 12 }}>
          <button onClick={exportToCSV}>Export CSV</button>
        </div>
        <div style={{ marginLeft: 12, minWidth: 260 }}>
          <div style={{ fontSize: 13, marginBottom: 6 }}>Resource (search)</div>
          <Typeahead
            items={resources.filter((v) =>
              rows.some((r) => r.resource === v.code),
            )}
            value={selectedResource}
            onChange={(it) => setSelectedResource(it)}
            placeholder="Type resource/user"
            getItemKey={(it) => it.code}
            getItemLabel={(it) => `${it.code} — ${it.desc}`}
            renderItem={(it) => (
              <div>
                <strong style={{ marginRight: 8 }}>{it.code}</strong>
                <span>{it.desc}</span>
              </div>
            )}
          />
        </div>
      </div>

      {loading ? (
        <p>Loading…</p>
      ) : error ? (
        <div style={{ color: "darkred" }}>{error}</div>
      ) : tableRows.length === 0 ? (
        <p>No Resource activity recorded.</p>
      ) : (
        <div style={{ overflowX: "visible" }}>
          <div style={{ marginBottom: 12 }}>
            <button onClick={toggleDisplayDays}>
              {displayDays ? "Show Totals in Hours" : "Show Totals in Days"}
            </button>
          </div>
          <div style={{ width: "100%", overflowX: "auto" }}>
            <div
              className="ag-theme-balham resource-pto-grid"
              style={{ width: "100%" }}
            >
              <AgGridReact
                ref={gridRef}
                rowData={tableRows}
                columnDefs={columnDefs}
                domLayout="autoHeight"
                rowStyle={{ backgroundColor: "transparent" }}
                defaultColDef={{
                  sortable: true,
                  filter: true,
                  resizable: true,
                  minWidth: 90,
                }}
                onColumnHeaderClicked={(event) => {
                  if (event.column?.getColId() === "totalMetric") {
                    toggleDisplayDays();
                  }
                }}
                onRowClicked={(event) => {
                  setSelectedResource(
                    resources.find(
                      (element) => element.code === event.data.resource,
                    ) || null,
                  );
                }}
              />
            </div>
          </div>

          {/* Month selector: sits below the table but above the charts. */}
          {selectedResource && (
            <div style={{ marginTop: 12, marginBottom: 12 }}>
              <label style={{ fontWeight: 600, marginRight: 8 }}>Month</label>
              <select
                value={selectedMonth || ""}
                onChange={(e) =>
                  setSelectedMonth(
                    e.target.value ? Number(e.target.value) : null,
                  )
                }
              >
                <option value="">All</option>
                <option value="1">January</option>
                <option value="2">February</option>
                <option value="3">March</option>
                <option value="4">April</option>
                <option value="5">May</option>
                <option value="6">June</option>
                <option value="7">July</option>
                <option value="8">August</option>
                <option value="9">September</option>
                <option value="10">October</option>
                <option value="11">November</option>
                <option value="12">December</option>
              </select>
              <label style={{ marginLeft: 8, color: "darkred" }}>
                {selectedLoading ? "Loading..." : ""}
              </label>
            </div>
          )}

          {selectedResource &&
            subcodeBreakdown &&
            subcodeBreakdown.length > 0 && (
              <ResourceChart
                selectedResource={selectedResource}
                subcodeBreakdown={subcodeBreakdown}
                projectSubcodeMatrix={projectSubcodeMatrix}
                year={year}
                month={selectedMonth}
                onClose={onClose}
                loading={setSelectedLoading}
              />
            )}
          {selectedResource &&
            subcodeBreakdown &&
            subcodeBreakdown.length === 0 && (
              <>
                <p>Selected resource has no activity for this period.</p>
              </>
            )}
        </div>
      )}
    </div>
  );
};

export default ResourceOverview;
